/**
 * SSO Provider Edit Modal
 *
 * Modal for editing an existing SSO provider configuration.
 * Allows updating client ID, client secret, domains, and provider-specific fields.
 */

import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { Checkbox } from '../../../components/ui/Checkbox';
import { FormField } from '../../../components/forms/FormField';
import { SSOProviderType } from '../../../types/sso.types';
import type { SSOProviderConfig, SSOProviderUpdateDTO } from '../../../types/sso.types';
import { useAuth } from '../../../contexts/AuthContext';
import { useRoles } from '../../roles/hooks/useRoles';

// ============================================================================
// TYPES
// ============================================================================

interface SSOProviderEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  provider: SSOProviderConfig | null;
  onSubmit: (providerId: string, data: SSOProviderUpdateDTO) => Promise<void>;
  isSubmitting?: boolean;
  error?: string;
}

interface EditFormValues {
  displayName: string;
  clientId: string;
  clientSecret: string;
  entraDirectoryId: string;
  hostedDomain: string;
  allowedDomains: string;
  enforceSSO: boolean;
  autoProvisionUsers: boolean;
  defaultRoles: string[];
}

// ============================================================================
// COMPONENT
// ============================================================================

export const SSOProviderEditModal: React.FC<SSOProviderEditModalProps> = ({
  isOpen,
  onClose,
  provider,
  onSubmit,
  isSubmitting = false,
  error,
}) => {
  const { tenant } = useAuth();
  const tenantId = tenant?.tenantId ?? '';
  const isMicrosoft = provider?.providerType === SSOProviderType.MICROSOFT_ENTRA;

  const { data: roles } = useRoles(tenantId);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<EditFormValues>();

  const autoProvisionUsers = watch('autoProvisionUsers');

  // Reset form when provider changes
  useEffect(() => {
    if (provider) {
      reset({
        displayName: provider.displayName || '',
        clientId: provider.clientId,
        clientSecret: '', // Never pre-fill secrets
        entraDirectoryId: provider.entraDirectoryId || '',
        hostedDomain: provider.hostedDomain || '',
        allowedDomains: (provider.allowedDomains || []).join(', '),
        enforceSSO: provider.enforceSSO ?? false,
        autoProvisionUsers: provider.autoProvisionUsers ?? false,
        defaultRoles: provider.defaultRoles ?? [],
      });
    }
  }, [provider, reset]);

  const handleFormSubmit = async (data: EditFormValues) => {
    if (!provider) return;

    const domains = data.allowedDomains
      .split(',')
      .map((d) => d.trim())
      .filter(Boolean);

    const updateData: SSOProviderUpdateDTO = {
      displayName: data.displayName || undefined,
      clientId: data.clientId || undefined,
      clientSecret: data.clientSecret || undefined,
      allowedDomains: domains.length > 0 ? domains : undefined,
      enforceSSO: data.enforceSSO,
      autoProvisionUsers: data.autoProvisionUsers,
      defaultRoles: data.autoProvisionUsers && data.defaultRoles?.length > 0
        ? data.defaultRoles
        : undefined,
      ...(isMicrosoft
        ? { entraDirectoryId: data.entraDirectoryId || undefined }
        : { hostedDomain: data.hostedDomain || undefined }),
    };

    // Remove empty strings to avoid overwriting with empty
    Object.keys(updateData).forEach((key) => {
      const k = key as keyof SSOProviderUpdateDTO;
      if (updateData[k] === undefined || updateData[k] === '') {
        delete updateData[k];
      }
    });

    await onSubmit(provider.providerId, updateData);
  };

  if (!provider) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Modal.Header
          title={`Edit ${isMicrosoft ? 'Microsoft Entra ID' : 'Google'} Configuration`}
        />
        <Modal.Body>
          <div className="space-y-4">
            {error && (
              <Alert variant="error">
                {error}
              </Alert>
            )}

            <FormField
              label="Display Name"
              error={errors.displayName?.message}
              helperText={`A label to identify this provider (e.g., "${isMicrosoft ? 'Microsoft - Acme Corp' : 'Google - Acme Corp'}"). Optional.`}
            >
              <Input
                {...register('displayName')}
                type="text"
                placeholder={isMicrosoft ? 'Microsoft Entra ID' : 'Google'}
                error={errors.displayName?.message}
              />
            </FormField>

            <FormField
              label="Client ID"
              error={errors.clientId?.message}
              helperText={isMicrosoft ? 'Application (client) ID from Azure Portal' : 'OAuth 2.0 Client ID'}
            >
              <Input
                {...register('clientId')}
                type="text"
                placeholder={isMicrosoft ? 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' : 'xxxxxxxxxxxx.apps.googleusercontent.com'}
                error={errors.clientId?.message}
              />
            </FormField>

            <FormField
              label="Client Secret"
              error={errors.clientSecret?.message}
              helperText="Leave empty to keep the existing secret"
            >
              <Input
                {...register('clientSecret')}
                type="password"
                placeholder="Enter new client secret (optional)"
                error={errors.clientSecret?.message}
              />
            </FormField>

            {isMicrosoft ? (
              <FormField
                label="Directory (Tenant) ID"
                error={errors.entraDirectoryId?.message}
                helperText="Your Azure AD tenant ID"
              >
                <Input
                  {...register('entraDirectoryId')}
                  type="text"
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  error={errors.entraDirectoryId?.message}
                />
              </FormField>
            ) : (
              <FormField
                label="Hosted Domain"
                error={errors.hostedDomain?.message}
                helperText="Restrict to a specific Google Workspace domain (optional)"
              >
                <Input
                  {...register('hostedDomain')}
                  type="text"
                  placeholder="example.com"
                  error={errors.hostedDomain?.message}
                />
              </FormField>
            )}

            <FormField
              label="Allowed Email Domains"
              error={errors.allowedDomains?.message}
              helperText="Comma-separated list of allowed email domains"
            >
              <Input
                {...register('allowedDomains')}
                type="text"
                placeholder="example.com, corp.example.com"
                error={errors.allowedDomains?.message}
              />
            </FormField>

            {/* SSO Settings */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">SSO Settings</h4>
              <div className="space-y-3">
                <Checkbox
                  {...register('enforceSSO')}
                  label="Enforce SSO"
                  helperText="When enabled, password login is disabled and users must sign in via this SSO provider."
                />

                <Checkbox
                  {...register('autoProvisionUsers')}
                  label="Auto-provision users"
                  helperText="Automatically create user accounts when new users sign in via SSO for the first time."
                />

                {autoProvisionUsers && roles && roles.length > 0 && (
                  <FormField
                    label="Default roles for auto-provisioned users"
                    helperText="Select roles to assign to users created via SSO auto-provisioning."
                  >
                    <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3">
                      {roles
                        .filter((r) => !r.isSystemRole)
                        .map((role) => (
                          <Checkbox
                            key={role.roleId}
                            {...register('defaultRoles')}
                            value={role.roleId}
                            label={role.roleName}
                            helperText={role.description}
                          />
                        ))}
                    </div>
                  </FormField>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default SSOProviderEditModal;
